// Path: ./src/app/pages/StorePage/index.tsx
import { Box, Stack } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import { SearchInput } from "../../components/SearchInputField";
import { adminApiService } from "../../../utils/api";
import {
  GfStore,
  MuiFilterRequestDto,
} from "@swagger/typescript-fetch-goflyer";

import { GridFilterModel, GridRowSelectionModel } from "@mui/x-data-grid";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { useTranslation } from "react-i18next";

import { messages } from "../../../locales/messages";
import { useNavigate } from "react-router-dom";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { TableData } from "../../../utils/interface/tableData";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { useStoreTableColumns } from "./useStoreTableColumns";
import {
  SetupPageContainer,
  SetupPageTopAppBar,
} from "../../../utils/commonStyledComponents/commonStyled";
import { BlackFontTransparentButton } from "../../../utils/commonStyledComponents/commonMuiStyled";
import { LeftButtonSection, RightSearchSection, TableWrapper } from "./styled";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { GoFlyerAdminContext } from "../../../contextApi/context";
import { SUPERADMIN } from "../../../utils/constant/stringConstant";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { ListTableQueryPagination } from "../../components/ListTable/ListTableQueyPagination";
import { muiFilterModelHasActiveItems } from "../../../utils/muiGridFilterModel";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";
import { useTableSettingsConfig } from "../../../hooks/useTableSettingsConfig";
import { SettingKey } from "@swagger/typescript-fetch-goflyer";
import { routes } from "../../../utils/constant/navigationRoutes";

const StorePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useContext(GoFlyerAdminContext);
  const authHeaders = useAuthHeaders();

  const { pageLimit } = useTableSettingsConfig(SettingKey.TableStores);

  const {
    paginationModel,
    sortModel,
    searchText,
    setQueryParams,
    handleTableChange,
  } = useQueryParams(pageLimit);

  const [paginationLoading, setPaginationLoading] = useState(false);
  const listRequestGuard = useLatestRequestGuard();
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);
  const [storeListData, setStoreListData] = useState<TableData<GfStore>>();
  const [open, setOpen] = useState(false);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const columns = useStoreTableColumns();
  /**
   * Opens a confirmation modal to delete a Upload Store.
   *
   * @param value - The Upload Store data to be deleted.
   */
  const handleOpenDelete = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const fetchStoreList = async () => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        const conditions = [];
        const storesList =
          state.user?.parsedRoles?.name === SUPERADMIN
            ? []
            : state.user?.stores?.map((value) => {
                return `${value.id}`;
              }) || [];

        if (storesList && storesList.length) {
          conditions.push(`id||$in||${storesList?.join(",")}`);
        }
        return adminApiService.getManyBaseGfStoreAdminControllerGfStore(
          undefined,
          undefined,
          conditions?.length ? conditions : undefined,
          undefined,
          sortModel?.length
            ? [`${sortModel[0].field},${sortModel[0]?.sort?.toUpperCase()}`]
            : [`updatedAt,DESC`],
          undefined,
          paginationModel?.pageSize,
          0,
          paginationModel?.page,
          undefined,
        );
      },
      (StoreList) => {
        setStoreListData((prev) => ({
          ...prev,
          rows: StoreList.data,
          total: StoreList?.total,
        }));
      },
    );
  };

  const fetchStoreListWithFilter = async (
    activeFilterModel: GridFilterModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        const body: MuiFilterRequestDto = {
          filterModel: {
            items: activeFilterModel.items.map((item) => ({
              field: item.field,
              operator: item.operator,
              value: item.value,
              id: item.id,
            })),
            logicOperator: activeFilterModel.logicOperator as any,
            quickFilterValues: activeFilterModel.quickFilterValues,
            quickFilterLogicOperator:
              activeFilterModel.quickFilterLogicOperator as any,
          },
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortModel: sortModel?.map((s) => ({
            field: s.field,
            sort: s.sort as any,
          })),
        };
        return adminApiService.gfStoreAdminControllerFilterWithMuiGrid(
          body,
          authHeaders,
        );
      },
      (response) => {
        if (!response) return;
        setStoreListData((prev) => ({
          ...prev,
          rows: response.rows,
          total: response.total,
        }));
      },
    );
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const handleDeleteStore = async () => {
    if (rowSelectionModel?.length) {
      try {
        setPaginationLoading(true);
        const result = await rowSelectionModel?.map(async (value) => {
          return await adminApiService.deleteOneBaseGfStoreAdminControllerGfStore(
            value as string,
            authHeaders,
          );
        });
        await Promise.all(result);

        if (searchText?.trim()?.length) {
          searchStoreFn(searchText);
        } else {
          fetchStoreList();
        }

        setPaginationLoading(false);
        handleCancel();
        toastSuccess(t(messages.Successfully_Deleted()));
      } catch (error) {
        console.log("error", error);
        setPaginationLoading(false);
        toastError(
          t(messages.There_is_an_error_please_Contact_Administrator()),
        );
      }
    }
  };

  const handleOnSearch = (value: string) => {
    setQueryParams({
      searchText: value,
      page: 1,
      sortField: null,
      sortOrder: null,
    });
  };

  const searchStoreFn = async (searchValue: string) => {
    if (!searchValue?.length) return;

    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () =>
        adminApiService.gfStoreAdminControllerSearch(
          searchValue,
          paginationModel.page,
          paginationModel.pageSize,
        ),
      (response) => {
        setStoreListData((prev) => ({
          ...prev,
          rows: response?.data,
          total: response?.total,
        }));
      },
    );
  };

  // The search endpoint can't sort, so search results are sorted in the grid.
  const isShowingSearchResults =
    !muiFilterModelHasActiveItems(filterModel) && Boolean(searchText?.length);

  useEffect(() => {
    const hasActiveFilters = muiFilterModelHasActiveItems(filterModel);
    if (hasActiveFilters) {
      fetchStoreListWithFilter(filterModel);
    } else if (searchText?.length) {
      searchStoreFn(searchText);
    } else {
      fetchStoreList();
    }
  }, [searchText, paginationModel, sortModel, filterModel]);

  return (
    <SetupPageContainer data-testid="storepage-test-id">
      <AccountCardWrapper title={"Store"}>
        <Box data-testid="view-page">
          <SetupPageTopAppBar>
            <LeftButtonSection>
              <Stack direction="row" spacing={2}>
                <PermissionComponentWrapper
                  requiredPermission={["create"]}
                  requiredPermissionGroup={["store"]}
                  wrapperCss={`
                           display:block;
                           margin-bottom:10px;
                           `}
                >
                  <BlackFontTransparentButton
                    variant="text"
                    startIcon={
                      <UploadFileIcon
                        style={{ color: "#ff8038", fontSize: 25 }}
                      />
                    }
                    onClick={() => {
                      navigate(routes.store.create);
                    }}
                    data-testid="create-button"
                  >
                    {t(messages.Create())}
                  </BlackFontTransparentButton>
                </PermissionComponentWrapper>
                <PermissionComponentWrapper
                  requiredPermission={["delete"]}
                  requiredPermissionGroup={["store"]}
                  wrapperCss={`
                           display:block;
                           margin-bottom:10px;
                           `}
                >
                  <BlackFontTransparentButton
                    variant="text"
                    startIcon={
                      <DeleteIcon
                        style={{
                          color: "#ff8038",
                          fontSize: 25,
                          opacity: !rowSelectionModel?.length ? 0.5 : 1,
                        }}
                      />
                    }
                    disabled={!rowSelectionModel?.length}
                    onClick={handleOpenDelete}
                    data-testid="delete-button"
                  >
                    {t(messages.Delete())}
                  </BlackFontTransparentButton>
                </PermissionComponentWrapper>
              </Stack>
            </LeftButtonSection>
            {(state.user?.parsedRoles?.name !== SUPERADMIN ||
              !state.user?.stores?.length) && (
              <RightSearchSection>
                <SearchInput
                  onSearch={handleOnSearch}
                  initialValue={searchText}
                ></SearchInput>
              </RightSearchSection>
            )}
          </SetupPageTopAppBar>
          <TableWrapper>
            <ListTableQueryPagination
              loadFailed={listRequestGuard.loadFailed}
              sortingMode={isShowingSearchResults ? "client" : "server"}
              columns={columns}
              rows={storeListData?.rows}
              total={paginationLoading ? 0 : storeListData?.total}
              paginationModel={paginationModel}
              sortModel={sortModel}
              isLoading={paginationLoading}
              onTableChange={handleTableChange}
              dynamicHeightAdjuster={230}
              handleRowSelection={(value) => {
                setRowSelectionModel(value);
              }}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
            />
          </TableWrapper>

          <MuiConfirmationModal
            open={open}
            handleCancel={handleCancel}
            handleConfirm={handleDeleteStore}
            title={`${t(messages.Delete_Confirmation())}`}
            description={`
        ${t(messages.Are_you_sure_you_want_to_delete_These())} ${t(
          messages.Selected_Stores(),
          { count: rowSelectionModel.length },
        )}?  ${t(messages.This_action_cannot_be_undone())}.
      `}
          />
        </Box>
      </AccountCardWrapper>
    </SetupPageContainer>
  );
};

export default StorePage;
